import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import MenuItem from '@mui/material/MenuItem'
import SearchIcon from '@mui/icons-material/Search'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import DesktopDatePicker from '@mui/lab/DesktopDatePicker'
import koLocale from 'date-fns/locale/ko'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, LIST_FILTER_DATE_FIELD_SX, LIST_FILTER_DIVIDER_SX, LIST_FILTER_SELECT_FIELD_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import ListActionGroup from '../../component/ListActionGroup'
import Snackbars from '../../component/Snackbars'
import StatusChip, { PAYMENT_STATUS_CHIP } from '../../component/StatusChip'
import Spiner from '../../component/Spiner'
import { buildListSearch as buildListSearchQuery, createInitialListState, parseDateParam } from '../../utils/listPageState'
import { formatDate, formatLocalDateTime as formatDateTime } from '../../utils/dateDisplay'
import { formatNumber } from '../../utils/numberDisplay'
import { formatPaymentShippingRegion, PAYMENT_SHIPPING_REGIONS } from './paymentShippingRegions'

const paymentDeviceMap = {
    1: 'web',
    2: 'and',
    3: 'ios',
    web: 'web',
    and: 'and',
    ios: 'ios',
    '': '-'
}

const DEFAULT_SEARCH_SELECT = {
    searchPayMethod: '',
    searchPayStatus: '',
    searchDeviceType: '0',
    searchShippingRegion: '',
}

const SEARCH_SELECT_PARAMS = {
    searchPayMethod: {
        param: 'payMethod',
        defaultValue: DEFAULT_SEARCH_SELECT.searchPayMethod,
    },
    searchPayStatus: {
        param: 'payStatus',
        defaultValue: DEFAULT_SEARCH_SELECT.searchPayStatus,
    },
    searchDeviceType: {
        param: 'deviceType',
        defaultValue: DEFAULT_SEARCH_SELECT.searchDeviceType,
    },
    searchShippingRegion: {
        param: 'shippingRegion',
        defaultValue: DEFAULT_SEARCH_SELECT.searchShippingRegion,
    },
}

const getInitialListState = (location) => {
    return createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
        getExtraState: ({ locationState, searchParams }) => {
            const initialStartTime = parseDateParam(searchParams.get('start'), locationState.startTime)
            const initialEndTimeCandidate = parseDateParam(searchParams.get('end'), locationState.endTime)
            const initialEndTime = initialEndTimeCandidate.getTime() < initialStartTime.getTime() ? initialStartTime : initialEndTimeCandidate

            return {
                startTime: initialStartTime,
                endTime: initialEndTime,
            }
        },
    })
}

const buildListSearch = ({ curPage, searchQuery, searchSelect, startTime, endTime }) => {
    const safeStartTime = parseDateParam(startTime)
    const safeEndTimeCandidate = parseDateParam(endTime)
    const safeEndTime = safeEndTimeCandidate.getTime() < safeStartTime.getTime() ? safeStartTime : safeEndTimeCandidate

    return buildListSearchQuery({
        curPage,
        searchQuery,
        searchSelect,
        selectParams: SEARCH_SELECT_PARAMS,
        extraParams: [
            {
                param: 'start',
                value: safeStartTime,
                serialize: formatDate,
                shouldInclude: () => true,
            },
            {
                param: 'end',
                value: safeEndTime,
                serialize: formatDate,
                shouldInclude: () => true,
            },
        ],
    })
}

const paymentSummaryTileSx = {
    minWidth: 0,
    p: 1.25,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    bgcolor: 'background.default',
}

const getProductOptionName = (row) => row.productOptionName || row.product_option?.name || ''

export default function Payments({ adminId, role }) {
    const currentYear = new Date().getFullYear()
    const minYear = currentYear - 2
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [totalCount, setTotalCount] = useState(0)
    const [data, setData] = useState([])
    const [plusPrice, setPlusPrice] = useState([])
    const [minus, setMinusPrice] = useState([])
    const [deviceSummary, setDeviceSummary] = useState({ webCount: 0, andCount: 0, iosCount: 0 })
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [startTime, setStartTime] = useState(initialListState.startTime)
    const [endTime, setEndTime] = useState(initialListState.endTime)
    const [listErrorMessage, setListErrorMessage] = useState('')

    const getDateText = (date) => {
        const safeDate = date && !isNaN(new Date(date).getTime()) ? new Date(date) : new Date()
        return formatDate(safeDate)
    }

    const getDeviceLabel = (deviceType) => {
        const target = deviceType || ''
        return paymentDeviceMap[target] || paymentDeviceMap[target.toString()] || paymentDeviceMap[Number(target)] || '-'
    }

    const claimSummaryCell = (row) => {
        const claimCount = Number(row.claimCount || 0)
        const pendingCount = Number(row.pendingClaimCount || 0)
        const completedCount = Number(row.completedClaimCount || 0)
        const rejectedCount = Number(row.rejectedClaimCount || 0)

        if (claimCount === 0) {
            return <StatusChip label="클레임 없음" color="success" />
        }

        return (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {pendingCount > 0 && <StatusChip label={`진행중 ${pendingCount}건`} color="warning" />}
                {completedCount > 0 && <StatusChip label={`완료 ${completedCount}건`} color="info" />}
                {rejectedCount > 0 && <StatusChip label={`반려 ${rejectedCount}건`} color="error" />}
                {row.latestClaimTypeName && (
                    <StatusChip
                        variant="outlined"
                        label={`최근 ${row.latestClaimTypeName}${row.latestClaimStatusName ? ` ${row.latestClaimStatusName}` : ''}`}
                    />
                )}
            </Box>
        )
    }

    const handleError = (message) => {
        setOpenSnackbar(true)
        setSeveritySnackbar('error')
        setMessageSnackbar(message || '요청 처리에 실패했습니다.')
    }

    const getData = useCallback(async () => {
        setLoading(true)
        setListErrorMessage('')

        try {
            const params = {
                page: curPage,
                offset: offset,
                adminId: adminId,
                role: role,
                searchText: (searchQuery || '').trim(),
                searchSelect: searchSelect || {},
                startTime: getDateText(startTime),
                endTime: getDateText(endTime),
            }

            const { data } = await API.post('/api/v1/admin/payment/getPaymentList', params)
            // console.log('getPaymentList data.data: ', data.data)
            if (data.code === 1) {
                setPlusPrice(data.data.sumPayData1?.[0]?.payAmount || 0)
                setMinusPrice(data.data.sumPayData2?.[0]?.cancelAmount || 0)
                setDeviceSummary({
                    webCount: data.data.deviceSummary?.webCount || 0,
                    andCount: data.data.deviceSummary?.andCount || 0,
                    iosCount: data.data.deviceSummary?.iosCount || 0,
                })
                setTotalCount(data.data.count || 0)
                setData(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setTotalCount(0)
                setData([])
                setDeviceSummary({ webCount: 0, andCount: 0, iosCount: 0 })
                setPlusPrice(0)
                setMinusPrice(0)
                setListErrorMessage(data.message || '결제 내역을 불러오지 못했습니다.')
                handleError(data.message)
            }
        } catch (error) {
            setTotalCount(0)
            setData([])
            setDeviceSummary({ webCount: 0, andCount: 0, iosCount: 0 })
            setPlusPrice(0)
            setMinusPrice(0)
            setListErrorMessage('결제 내역을 불러오지 못했습니다.')
            handleError('결제 내역을 불러오지 못했습니다.')
        } finally {
            setLoading(false)
        }
    }, [adminId, curPage, endTime, history, role, searchQuery, searchSelect, startTime])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearch({ curPage, searchQuery, searchSelect, startTime, endTime })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, endTime, history, location.pathname, location.search, searchQuery, searchSelect, startTime])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const searchButtonClick = () => {
        if (loading) {
            return
        }

        setSearchQuery((searchText || '').trim())
        setCurPage(1)
    }

    const resetFilter = () => {
        if (loading) {
            return
        }

        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
        })
        setStartTime(new Date())
        setEndTime(new Date())
        setCurPage(1)
    }

    const updateSearchSelect = (e) => {
        if (loading) {
            return
        }

        setSearchSelect({
            ...searchSelect,
            [e.target.name]: e.target.value
        })
    }

    const updateShippingRegion = (e) => {
        if (loading) {
            return
        }

        setSearchSelect({
            ...searchSelect,
            searchShippingRegion: e.target.value,
        })
        setCurPage(1)
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    // 결제 목록은 기본 날짜가 오늘이므로 검색어, 선택 필터, 날짜 변경 여부를 함께 본다.
    const hasActiveFilters = () => {
        if ((searchQuery || '').trim()) {
            return true
        }

        const hasChangedSelect = Object.entries(DEFAULT_SEARCH_SELECT).some(([key, defaultValue]) => {
            const currentValue = searchSelect?.[key] ?? ''
            const normalizedDefaultValue = defaultValue ?? ''

            return String(currentValue) !== String(normalizedDefaultValue)
        })

        if (hasChangedSelect) {
            return true
        }

        const todayText = formatDate(new Date())

        return getDateText(startTime) !== todayText || getDateText(endTime) !== todayText
    }

    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="결제 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button type="button" size="small" variant="outlined" onClick={getData} disabled={loading}>
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters()) {
            return (
                <ListEmptyState
                    title="조건에 맞는 결제가 없습니다."
                    description="검색어, 날짜 또는 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilter} disabled={loading} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="조회된 결제 내역이 없습니다."
                description="결제 내역이 발생하면 이 목록에 표시됩니다."
            />
        )
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    {/* 타이틀 */}
                    <Title>결제 리스트</Title>
                    {/* 검색폼 */}
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilter} disabled={loading} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="주문번호/상품명/구매자/userId"
                                type="search" 
                                name="searchText"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                disabled={loading}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={loading}><SearchIcon /></IconButton>
                            <Divider sx={LIST_FILTER_DIVIDER_SX} orientation="vertical" />
                            <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                <DesktopDatePicker
                                    label="시작일"
                                    inputFormat="yyyy-MM-dd"
                                    mask={"____-__-__"}
                                    minDate={new Date(minYear, 0, 1)}
                                    maxDate={new Date()}
                                    value={startTime}
                                    onChange={d => setStartTime(d)}
                                    disabled={loading}
                                    renderInput={(params) => <TextField required {...params} size="small" sx={LIST_FILTER_DATE_FIELD_SX} disabled={loading} />}
                                />
                            </LocalizationProvider>&nbsp;~&nbsp;
                            <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                <DesktopDatePicker
                                    label="종료일"
                                    inputFormat="yyyy-MM-dd"
                                    mask={"____-__-__"}
                                    value={endTime}
                                    minDate={startTime}
                                    maxDate={new Date()}
                                    onChange={d => setEndTime(d)}
                                    disabled={loading}
                                    renderInput={(params) => <TextField required {...params} size="small" sx={LIST_FILTER_DATE_FIELD_SX} disabled={loading} />}
                                />
                            </LocalizationProvider>
                            <Divider sx={LIST_FILTER_DIVIDER_SX} orientation="vertical" />
                            <TextField
                                label="결제기기"
                                select
                                name="searchDeviceType"
                                value={searchSelect.searchDeviceType}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={loading}
                                sx={LIST_FILTER_SELECT_FIELD_SX}
                            >
                                <MenuItem value='0'>전체</MenuItem>
                                <MenuItem value={'1'}>web</MenuItem>
                                <MenuItem value={'2'}>and</MenuItem>
                                <MenuItem value={'3'}>ios</MenuItem>
                            </TextField>
                            <TextField
                                label="배송지역"
                                select
                                name="searchShippingRegion"
                                value={searchSelect.searchShippingRegion}
                                onChange={updateShippingRegion}
                                size="small"
                                disabled={loading}
                                sx={LIST_FILTER_SELECT_FIELD_SX}
                            >
                                <MenuItem value=''>전체</MenuItem>
                                {PAYMENT_SHIPPING_REGIONS.map((region) => (
                                    <MenuItem key={region} value={region}>{region}</MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="결제수단"
                                select
                                name="searchPayMethod"
                                value={searchSelect.searchPayMethod}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={loading}
                                sx={LIST_FILTER_SELECT_FIELD_SX}
                            >
                                <MenuItem value=''>전체</MenuItem>
                                <MenuItem value={'card'}>신용카드</MenuItem>
                                <MenuItem value={'vbank'}>가상계좌</MenuItem>
                            </TextField>
                            <TextField
                                label="결제상태"
                                select
                                name="searchPayStatus"
                                value={searchSelect.searchPayStatus}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={loading}
                                sx={LIST_FILTER_SELECT_FIELD_SX}
                            >
                                <MenuItem value=''>전체</MenuItem>
                                <MenuItem value={'paid'}>결제</MenuItem>
                                <MenuItem value={'cancelled'}>취소</MenuItem>
                            </TextField>
                        </ListFilterForm>
                    </ListFilterBar>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
                            gap: 1,
                            mb: 1.5,
                        }}
                    >
                        <Box sx={paymentSummaryTileSx}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                기기별 결제
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700 }}>
                                web {formatNumber(deviceSummary.webCount)} / and {formatNumber(deviceSummary.andCount)} / ios {formatNumber(deviceSummary.iosCount)}
                            </Typography>
                        </Box>
                        <Box sx={paymentSummaryTileSx}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                총 거래액
                            </Typography>
                            <Typography variant="subtitle1" sx={{ mt: 0.5, fontWeight: 800 }}>
                                {formatNumber(plusPrice)}
                            </Typography>
                        </Box>
                        <Box sx={paymentSummaryTileSx}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                총 환불액
                            </Typography>
                            <Typography variant="subtitle1" color="error.main" sx={{ mt: 0.5, fontWeight: 800 }}>
                                -{formatNumber(minus)}
                            </Typography>
                        </Box>
                        <Box sx={paymentSummaryTileSx}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                총 매출액
                            </Typography>
                            <Typography variant="subtitle1" color="primary.main" sx={{ mt: 0.5, fontWeight: 800 }}>
                                {formatNumber(plusPrice - minus)}
                            </Typography>
                        </Box>
                    </Box>
                    
                    {/* 테이블 */}
                    <ListTableContainer>
                    <Table size="small" sx={{ minWidth: 1200 }}>
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>상세주문번호</TableCell>
                                <TableCell>상품명<br/>옵션명</TableCell>
                                <TableCell>기종</TableCell>
                                <TableCell>수량</TableCell>
                                <TableCell>상품<br/>금액</TableCell>
                                <TableCell>결제/취소<br/>금액</TableCell>
                                <TableCell>할인<br/>금액</TableCell>
                                <TableCell>수단</TableCell>
                                <TableCell>구매자/배송지</TableCell>
                                <TableCell>상태</TableCell>
                                <TableCell>결제/취소일</TableCell>
                                <TableCell>구매<br/>건수</TableCell>
                                <TableCell>가입일</TableCell>
                                <TableCell>클레임</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.length > 0 ? data.map((row,i) => (
                                <TableRow key={i}>
                                    <TableCell>{row.rownum}</TableCell>
                                    <TableCell>{row.orderDetailId}</TableCell>
                                    <TableCell>
                                        {row.title}<br/>
                                        {row.name}{getProductOptionName(row) ? `, ${getProductOptionName(row)}` : ''}
                                    </TableCell>
                                    <TableCell>{getDeviceLabel(row.deviceType)}</TableCell>
                                    <TableCell>{row.quantity}</TableCell>
                                    <TableCell>{formatNumber(row.totalPrice)}</TableCell>
                                    {row.payStatus === 'paid' ?
                                        <TableCell>{formatNumber(row.payAmount)}</TableCell>
                                    :
                                        <TableCell style={{color:'red'}}>-{formatNumber(row.cancelAmount)}</TableCell>
                                    }
                                    <TableCell>{formatNumber(row.discountPrice)}</TableCell>
                                    <TableCell>{row.payMethod === 'card' ? '신용카드' : '가상계좌' }</TableCell>
                                    <TableCell>
                                        {row.buyerName || '-'}<br/>
                                        {formatPaymentShippingRegion(row.shippingRegion)}
                                    </TableCell>
                                    <TableCell><StatusChip status={row.payStatus} config={PAYMENT_STATUS_CHIP} /></TableCell>
                                    <TableCell>{formatDateTime(row.regTime)}</TableCell>
                                    <TableCell>{row.payCnt}</TableCell>
                                    <TableCell>{formatDateTime(row.joinTime)}</TableCell>
                                    <TableCell>{claimSummaryCell(row)}</TableCell>
                                    <TableCell align="right">
                                        <ListActionGroup>
                                            <EditIcon path={'payment'} id={row.orderDetailId} search={location.search} curPage={curPage} searchText={searchQuery} searchSelect={searchSelect} startTime={startTime} endTime={endTime} disabled={loading}/>
                                        </ListActionGroup>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={16}>
                                        {renderEmptyState()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
